import React, { useState } from 'react';
import { Step } from 'react-joyride';
import { useHistory } from 'react-router-dom';

import AppBar from 'components/AppBar';
import FloatingButton from 'components/FloatingButton';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Item from 'components/List/StyledListItem';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Search from 'components/Search';
import Tour from 'components/Tour';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { htmlT } from 'utils/generic';
import { TravelReason } from 'models/travelReseller';
import BreakRulesReasonEmptyList from './components/BreakRulesReasonEmptyList';
import { List, Container } from './styles';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import { deleteBreaRulesReason } from 'services/resellerTravel';
import useToast from 'hooks/useToast';
import { CloseButton, CloseIcon } from 'components/ModalHeader/styles';

interface Props {
  breakRulesTarget?: string;
  breakRulesReasonList: TravelReason[];
  setBreakRulesReasonList: React.Dispatch<React.SetStateAction<TravelReason[]>>;
  isLoading: boolean;
  tourHasAccess?: boolean;
  tourLoading: boolean;
  ruleSelected?: TravelReason;
  onCloseSelectionScreen?: () => void;
  onConfirmSelection?: (rule: TravelReason) => void;
  onDeleteSuccess?: () => void;
}

const BreakRulesReasonList = ({ breakRulesReasonList, setBreakRulesReasonList, isLoading, tourLoading, onDeleteSuccess }: Props) => {
  const [showSearch, setShowSearch] = useState(false);
  const [breakRulesResult, setBreakRulesResult] = useState<TravelReason[]>([]);
  const [breakRulesReasonSelected, setBreakRulesReasonSelected] = useState<TravelReason | undefined>(undefined);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const toast = useToast();
  const history = useHistory();
  const { t } = useStoreTranslation();
  const breakRulesData = { isAddButton: true };
  /* estados globales */
  const accountResult = useSelector((state: RootState) => state.user.accountResult);

  const handleSelection = (breakRules: TravelReason) => {
    console.log(breakRules)
    history.replace(`/corporate/breakRulesForm/${breakRules.id}`);
  };

  const handleOnSearch = (value: string) => {
    if (value) {
      setShowSearch(true);
      setBreakRulesResult(
        breakRulesReasonList.filter(
          (name: TravelReason) => name.name && name.name.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    } else {
      setShowSearch(false);
    }
  };
  const handleDelete = async () => {
    try {
      const data = await deleteBreaRulesReason(breakRulesReasonSelected?.id);
      if (data === 'ok') {
        toast.add({
          type: 'success',
          message: t('resellerForm.delete.success'),
        });
        onDeleteSuccess?.();
      }
    } catch (error) {}
  };

  const handleConfirmDelete = (breakRules?: TravelReason) => {
    if (breakRulesReasonList.length === 1) {
      toast.add({ type: 'danger', message: t('resellerForm.deleteLastReason.error') });
      return;
    }
    setShowConfirmDelete(true);
    setBreakRulesReasonSelected(breakRules);
  };

  const renderList = () => (
    <>
      <Search show={breakRulesReasonList.length >= 10} onSearch={handleOnSearch} />

      <Mobile>
        {showSearch ? (
          <List>
            <GroupedItem
              quantityText={htmlT(t, 'groupList.searchResults', { count: breakRulesResult?.length })}
              isSearch={showSearch}
              items={breakRulesResult}
              selectableType="arrow"
              onSelection={handleSelection}
            />
          </List>
        ) : (
          <>
            <GroupedList
              isAlphabetic
              isSelectable={false}
              items={breakRulesReasonList}
              isSearch={showSearch}
              selectableType="arrow"
              onSelection={handleSelection}
            />
          </>
        )}
      </Mobile>

      <Desktop>
        <List>
          {(showSearch ? breakRulesResult : breakRulesReasonList).map((breakRules) => (
            <Item
              key={`user-${breakRules.id}`}
              title={breakRules.name || ''}
              deleteMode={!breakRules.isFromReseller}
              forceEdit={!breakRules.isFromReseller}
              disabled={breakRules.isFromReseller}
              onDelete={() => handleConfirmDelete(breakRules)}
              onClick={() => handleSelection(breakRules)}
            />
          ))}
        </List>
        <ConfirmBottomSheet
          show={showConfirmDelete}
          title={t('resellerForm.confirmDelete.title')}
          onClose={() => setShowConfirmDelete(false)}
          content={t('resellerForm.confirmDelete.text')}
          confirmText={t('rule.delete.bottomSheetButton')}
          onConfirm={() => handleDelete()}
        />
      </Desktop>
    </>
  );
  const initialSteps: Step[] = [
    {
      target: '.target',
      content: t('groupList.addButtonTour'),
      disableBeacon: true,
      placement: 'center',
    },
  ];

  return (
    <>
      <Mobile>
        <AppBar goBack={() => history.replace('/corporate/configurations')}>
          <h5>{t('breakRulesReason.title')}</h5>
        </AppBar>
      </Mobile>

      {isLoading ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader
              title={t('breakRulesReason.title')}
              subtitle={t('breakRulesReason.description')}
              nameCompany={t('breakRulesReason.nameCompany', { company: accountResult.companyName })}
              right={
                <CloseButton onClick={() => history.goBack()}>
                  <CloseIcon />
                </CloseButton>
              }
            />
          </Desktop>

          <Container hasList={breakRulesReasonList.length > 0}>
            <Tour
              type="cancelable"
              show={!tourLoading /* && !tourHasAccess */}
              hideArrow
              disableOverlayClose
              disableScrollParentFix
            />
            {breakRulesReasonList && breakRulesReasonList.length ? renderList() : <BreakRulesReasonEmptyList />}
          </Container>
        </>
      )}
      <FloatingButton
        zIndex={2}
        onClick={() => history.replace({ pathname: '/corporate/breakRulesForm', state: { breakRulesData } })}
        className="target"
      />
    </>
  );
};

export default BreakRulesReasonList;
